/**
 * Package tier + audience data for the landing "Packages" section.
 *
 * Extracted from Packages.jsx so pricing/copy edits don't require
 * touching component code (and so tests can import the raw data).
 *
 * Structure:
 *   AUDIENCES  → the top-level "For Creators" / "For Brands" pills.
 *   Each audience has a `tiers` array (its 3 packages).
 *   `ADD_ONS`  → the small à-la-carte upsell strip shown under Creators.
 */
import {
  Camera,
  Crown,
  Headphones,
  Rocket,
  Users,
  Zap,
} from "lucide-react";

export const CREATOR_TIERS = [
  {
    name: "Studio Day",
    pkg: "studio_day",
    badge: "Starter",
    price: "600",
    unit: "/ session",
    icon: Rocket,
    description:
      "For creators who just need the space and gear — no frills, full quality.",
    cta: "Book a studio day",
    features: [
      { label: "Up to 2-hour studio session", in: true },
      { label: "Multi-camera video setup", in: true },
      { label: "Professional audio (mics + monitoring)", in: true },
      { label: "Studio lighting included", in: true },
      { label: "Editing & post-production", in: true },
      { label: "Files delivered within 5 business days", in: true },
      { label: "Short-form clips", in: false },
    ],
    accent: "#a395c3",
    highlight: false,
  },
  {
    name: "Creator Launch",
    pkg: "creator_launch",
    badge: "Most popular",
    price: "1,050",
    unit: "/ episode",
    icon: Zap,
    description:
      "Studio session plus edited content ready to post — your complete release workflow.",
    cta: "Launch my next episode",
    features: [
      { label: "Up to 2-hour studio session", in: true },
      { label: "Multi-camera video setup", in: true },
      { label: "Professional audio (mics + monitoring)", in: true },
      { label: "Full episode edit (audio + video)", in: true },
      { label: "3 short-form clips (Reels / TikTok)", in: true },
      { label: "Branded intro/outro added", in: true },
      { label: "Delivered within 5 business days", in: true },
    ],
    accent: "#00f0ff",
    highlight: true,
  },
  {
    name: "Content Partner",
    pkg: "content_partner",
    badge: "Premium",
    price: "1,550",
    unit: "/ month",
    icon: Crown,
    description:
      "Monthly retainer for creators who need consistent, professional output every month.",
    cta: "Become a partner",
    features: [
      { label: "2 studio sessions per month", in: true },
      { label: "2 full episode edits (audio + video)", in: true },
      { label: "6 short-form clips total", in: true },
      { label: "1 headshot / brand photo session", in: true },
      { label: "Priority scheduling", in: true },
      { label: "Monthly content strategy call", in: true },
      { label: "Analytics strategies", in: true },
    ],
    accent: "#ff0055",
    highlight: false,
  },
];

export const BRAND_TIERS = [
  {
    name: "Brand Shoot Day",
    pkg: "brand_shoot_day",
    badge: "One-time shoot",
    price: "850",
    unit: "/ one-time",
    icon: Camera,
    description:
      "One shoot, a full library of professional assets — their first taste of the studio.",
    cta: "Book a brand shoot",
    features: [
      { label: "Half-day shoot (up to 3 hrs), studio or on-site", in: true },
      { label: "Team or individual headshots", in: true },
      { label: "Product, service & space photography", in: true },
      { label: "15–20 edited photos + 3 short-form clips", in: true },
      { label: "Delivered within 5 business days", in: true },
      { label: "Files ready for web & social", in: true },
    ],
    accent: "#a395c3",
    highlight: false,
  },
  {
    name: "Content Engine",
    pkg: "content_engine",
    badge: "Most popular",
    price: "1,500",
    unit: "/ month",
    icon: Zap,
    description:
      "A month of ready-to-post content, every month. You produce, they post.",
    cta: "Start the engine",
    features: [
      { label: "1 monthly content shoot (up to 3 hrs)", in: true },
      { label: "8 edited social posts (photo + graphic)", in: true },
      { label: "4 short-form videos (Reels / TikTok)", in: true },
      { label: "Captions written for every piece", in: true },
      { label: "Monthly content plan — what to post, when", in: true },
      { label: "Delivered ready-to-publish", in: true },
    ],
    accent: "#00f0ff",
    highlight: true,
  },
  {
    name: "Social Command",
    pkg: "social_command",
    badge: "Full management",
    price: "3,000",
    unit: "/ month",
    icon: Crown,
    description: "We run their media end to end. They run their business.",
    cta: "Hand it over",
    features: [
      { label: "Everything in Content Engine", in: true },
      { label: "Full posting & scheduling across platforms", in: true },
      { label: "Community management (comments & DMs)", in: true },
      { label: "Monthly strategy + analytics report", in: true },
      { label: "Quarterly brand photo refresh", in: true },
      { label: "Priority turnaround + dedicated line", in: true },
    ],
    accent: "#ff0055",
    highlight: false,
  },
];

export const ADD_ONS = [
  { label: "Extra short-form clips", price: "$75 / clip" },
  { label: "Headshot session (standalone)", price: "$200" },
  { label: "Rush delivery (48-hr turnaround)", price: "+$150" },
];

export const AUDIENCES = [
  {
    id: "creators",
    label: "For Creators",
    sub: "Podcasters · Content creators",
    icon: Headphones,
    tiers: CREATOR_TIERS,
    headline: "Built for creators.",
    subhead:
      "Whether you're just getting started or ready to scale your content, we have a package that fits your goals and your grind.",
    showAddOns: true,
  },
  {
    id: "brands",
    label: "For Brands",
    sub: "Small business · Service brands",
    icon: Users,
    tiers: BRAND_TIERS,
    headline: "Built for brands.",
    subhead:
      "Not every business needs a podcast. Some just need to look like they have a marketing team. Whatever stage you're at, there's a package built for how your business grows.",
    showAddOns: false,
  },
];
